import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchGql } from '@/lib/graphql_client';
import { MyPermissionsDocument } from '@/gql/graphql';
import { useContextStore } from '@/lib/session/context-store';
import type { AclRoute } from './acl-routes';

export interface UsePermissionsResult {
  isLoading: boolean;
  isError: boolean;
  /**
   * Resolved key set, or `null` while loading / on error. `null` drives the
   * fail-closed behaviour of `checkPermission`.
   */
  keys: Set<string> | null;
  /**
   * FAIL-CLOSED: `true` only when the keys have resolved AND contain `key`.
   * While loading, on error, or with no active context → `false`.
   *
   * ⚠️ UX only — never a security boundary. The BE authorizes every real call.
   */
  checkPermission: (key: string) => boolean;
}

/**
 * Fetches the caller's UI permission-keys for an ACL route (acl-svc
 * `myPermissions(route)`) and exposes a fail-closed `checkPermission`.
 *
 * Requires an active context (the query is `enabled` only when one is set —
 * otherwise the BE returns `ACL_CONTEXT_REQUIRED`). Keys are per-context, so the
 * query key includes the active tenant id; invalidate `['myPermissions']` on
 * `switchContext` to refetch.
 *
 * `route` is the module/route namespace (see {@link AclRoute}).
 *
 * Every persona, Platform roles included, is decided by `myPermissions` — no
 * persona short-circuit (ADR-026). A genuine Super Admin gets keys from the
 * platform-admin role's own grants; a support (acting-as) session gets its
 * read-only acting-as bundle. Loading and error resolve `checkPermission` to
 * `false`, as for any other persona.
 */
export function usePermissions(route: AclRoute): UsePermissionsResult {
  const tenantId = useContextStore((s) => s.active?.tenantId);

  const query = useQuery({
    queryKey: ['myPermissions', route, tenantId ?? 'none'],
    queryFn: async (): Promise<Set<string>> => {
      const data = await fetchGql(MyPermissionsDocument, { route });
      return new Set(data.myPermissions ?? []);
    },
    enabled: !!tenantId,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });

  const keys = query.data ?? null;

  const checkPermission = useCallback(
    (key: string): boolean => keys?.has(key) ?? false,
    [keys],
  );

  return {
    isLoading: query.isLoading,
    isError: query.isError,
    keys,
    checkPermission,
  };
}
